import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getRetailerTransactionLog } from "../api/api";

interface Transaction {
  transactions_id: number;
  beneficiary_id?: number;
  transaction_id: string;
  user_amount: string;
  user_amount_with_charge: string;
  opening_balance?: string;
  closing_balance?: string;
  commission?: string;
  transaction_status: string;
  transaction_date: string;
  retailer?: {
    name?: string;
    mobile_number?: string;
  };
  beneficiary?: {
    account_holder_name?: string;
    bank_name?: string;
    account_number?: string;
    ifsc_code?: string;
    customer?: {
      customer_name?: string;
      mobile_number?: string;
    };
  };
}

const PER_PAGE = 5;

function fmt(n: number) {
  return "₹" + n.toLocaleString("en-IN");
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, React.CSSProperties> = {
    Success: { background: "#eaf3de", color: "#3b6d11", border: "1px solid #97c459" },
    Failed:  { background: "#fcebeb", color: "#a32d2d", border: "1px solid #f09595" },
    Pending: { background: "#faeeda", color: "#854f0b", border: "1px solid #ef9f27" },
  };
  const s = styles[status] || styles.Pending;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 500, padding: "4px 10px", borderRadius: 20, ...s }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />
      {status}
    </span>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 120, background: "#f9fafb", borderRadius: 8, padding: "12px 16px", border: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || "#111827" }}>{value}</div>
    </div>
  );
}

export default function RetailerTransactionLogPage() {
  const { user } = useAuth() as any;
  const location = useLocation() as any;
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Transaction | null>(null);

  const [refSearch, setRefSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const invoiceRef = useRef<HTMLDivElement | null>(null);

  const selectedRetailerLoginId = location?.state?.retailerLoginId ?? location?.state?.login_id ?? location?.state?.retailerId ?? user?.id;

  const fetchTransactions = useCallback(async () => {
    if (!user?.token || !selectedRetailerLoginId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        login_id: selectedRetailerLoginId,
        search: refSearch || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        status: statusFilter || undefined,
      };

      const body = await getRetailerTransactionLog(payload, user.token);
      if (!body?.success) {
        throw new Error(body?.message || "Failed to fetch retailer transaction log");
      }

      const responseData = body.data ?? body;
      const fetchedTransactions: Transaction[] = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData?.transactions)
        ? responseData.transactions
        : [];

      setTransactions(fetchedTransactions);
    } catch (err: unknown) {
      setTransactions([]);
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setLoading(false);
    }
  }, [user?.token, selectedRetailerLoginId, refSearch, fromDate, toDate, statusFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const filtered = useMemo(() => {
    return transactions;
  }, [transactions]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const successCount = filtered.filter((t) => t.transaction_status?.toLowerCase() === "success").length;
  const failedCount = filtered.filter((t) => t.transaction_status?.toLowerCase() === "failed").length;
  const pendingCount = filtered.filter((t) => t.transaction_status?.toLowerCase() === "pending").length;
  const partialFailedCount = filtered.filter((t) =>
    t.transaction_status?.toLowerCase() === "partially-failed" || t.transaction_status?.toLowerCase() === "partial_failed"
  ).length;
  const totalAmt = filtered
    .filter((t) => t.transaction_status?.toLowerCase() === "success")
    .reduce((sum, t) => sum + Number(t.user_amount ?? 0), 0);

  const requestAmount = Number(selectedInvoice?.user_amount ?? 0);
  const serviceFeeAmount = Math.round(requestAmount * 0.01 * 100) / 100;
  const totalPayableAmount = requestAmount + serviceFeeAmount;

  function clearFilters() {
    setRefSearch("");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setPage(1);
  }

  function exportCSV() {
    const rows = [
      ["Transaction ID", "Date", "Beneficiary", "Bank", "Account", "Amount", "Charge", "Status"],
      ...filtered.map((t) => [
        t.transaction_id,
        t.transaction_date,
        t.beneficiary?.account_holder_name ?? "",
        t.beneficiary?.bank_name ?? "",
        t.beneficiary?.account_number ?? "",
        t.user_amount,
        t.user_amount_with_charge,
        t.transaction_status,
      ]),
    ];
    const csv = rows.map((r) => r.map((cell) => String(cell).replace(/\n/g, " ")).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = "retailer_transaction_log.csv";
    a.click();
  }

  async function handleDownloadInvoicePdf(transaction: Transaction) {
    if (!invoiceRef.current) return;
    const canvas = await html2canvas(invoiceRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const pdf = new jsPDF({ unit: 'px', format: 'a4' });
    const imgData = canvas.toDataURL('image/png');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
    const imgWidth = canvas.width * ratio;
    const imgHeight = canvas.height * ratio;
    pdf.addImage(imgData, 'PNG', (pageWidth - imgWidth) / 2, 20, imgWidth, imgHeight);
    pdf.save(`invoice-${transaction.transaction_id || 'report'}.pdf`);
  }

  function handlePrintInvoice(transaction: Transaction) {
    if (!invoiceRef.current) return;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;
    const invoiceHtml = invoiceRef.current.outerHTML;

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${transaction.transaction_id}</title>
          <style>
            body { font-family: 'Inter', 'Segoe UI', sans-serif; margin: 0; padding: 20px; color: #111827; background: #f9fafb; }
            .invoice-container { width: 100%; max-width: 760px; margin: auto; }
            .invoice-container * { box-sizing: border-box; }
            .invoice-container div { page-break-inside: avoid; }
            .invoice-container { background: #fff; border-radius: 24px; padding: 28px; }
            .invoice-container .receipt-card { background: #f8fafc; border-radius: 16px; padding: 18px; }
            .invoice-container .receipt-card h3 { margin: 0; }
            .invoice-container .no-print { display: none !important; }
          </style>
          <script>
            window.onload = function() {
              window.focus();
              setTimeout(function() {
                window.print();
                window.close();
              }, 200);
            };
          </script>
        </head>
        <body>
          <div class="invoice-container">
            ${invoiceHtml}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  }

  const inputStyle: React.CSSProperties = {
    fontSize: 13, height: 36, padding: "0 10px",
    border: "1px solid #e5e7eb", borderRadius: 6,
    background: "#fff", color: "#111827", outline: "none",
  };

  const colGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1.8fr 1.2fr 2fr 0.8fr 0.8fr 120px",
    gap: 16,
    alignItems: "center",
  };

  return (
    <div style={{ padding: "28px 32px", fontFamily: "'Inter', 'Segoe UI', sans-serif", width: "100%", boxSizing: "border-box", background: "#f9fafb", minHeight: "100vh" }}>

      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: "#111827", margin: 0 }}>DMT Transaction log</h2>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20, alignItems: "center" }}>
        <input
          style={{ ...inputStyle, width: 220 }}
          placeholder="Search transaction / beneficiary"
          value={refSearch}
          onChange={(e) => { setRefSearch(e.target.value); setPage(1); }}
        />
        <input
          type="date"
          style={{ ...inputStyle, width: 150 }}
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
        />
        <input
          type="date"
          style={{ ...inputStyle, width: 150 }}
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(1); }}
        />
        <select
          style={{ ...inputStyle, width: 140 }}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
        <button
          onClick={clearFilters}
          style={{ height: 36, padding: "0 14px", fontSize: 13, background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 6, cursor: "pointer" }}
        >
          Clear
        </button>
        <button
          onClick={exportCSV}
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "7px 14px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", color: "#374151", cursor: "pointer", fontWeight: 500, marginLeft: 4 }}
        >
          ⬇ Export to Excel
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <SummaryCard label="Total transactions" value={filtered.length} />
        <SummaryCard label="Successful" value={successCount} color="#15803d" />
        <SummaryCard label="Failed" value={failedCount} color="#b91c1c" />
        <SummaryCard label="Pending" value={pendingCount} color="#92400E" />
        {partialFailedCount > 0 && <SummaryCard label="Partial failed" value={partialFailedCount} color="#d97706" />}
        <SummaryCard label="Total transferred" value={fmt(totalAmt)} />
      </div>

      {error && (
        <div style={{ color: "#b91c1c", marginBottom: 16 }}>{error}</div>
      )}
      {loading && (
        <div style={{ color: "#6b7280", marginBottom: 16 }}>Loading transactions…</div>
      )}

      {/* Table header */}
      <div style={{ ...colGrid, padding: "0 20px 10px", borderBottom: "1px solid #e5e7eb", marginBottom: 8 }}>
        {["Transaction", "Sender", "Beneficiary", "Charges", "Amount", "Status"].map((h) => (
          <span key={h} style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{h}</span>
        ))}
      </div>

      {/* Transaction rows */}
      {paginated.length === 0 ? (
        <div style={{ textAlign: "center", padding: "4rem 1rem", color: "#9ca3af", fontSize: 14 }}>
          {loading ? "Loading transactions…" : "No transactions found"}
        </div>
      ) : (
        paginated.map((t) => {
          const senderMobile = t.beneficiary?.customer?.mobile_number || "—";
          const beneficiaryName = t.beneficiary?.account_holder_name || t.beneficiary?.customer?.customer_name || "—";
          const displayBank = t.beneficiary?.bank_name || "—";
          const displayAcct = t.beneficiary?.account_number || "—";
          const displayIfsc = t.beneficiary?.ifsc_code || "—";
          const displayDate = t.transaction_date
            ? new Date(t.transaction_date).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—";
          const amount = Number(t.user_amount) || 0;
          const debit = Number(t.user_amount_with_charge) || 0;

          return (
            <div
              key={t.transactions_id}
              style={{
                ...colGrid,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "16px 20px",
                marginBottom: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
                  Transaction
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>{t.transaction_id}</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{displayDate}</div>
              </div>

              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{senderMobile}</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2, fontWeight: 600 }}>{t.beneficiary?.customer?.customer_name || "—"}</div>
              </div>

              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{beneficiaryName}</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2, fontWeight: 600 }}>{displayBank}</div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1, fontWeight: 600 }}>{displayAcct}</div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1, fontWeight: 600 }}>{displayIfsc}</div>
              </div>

              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>Charges</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{fmt(Number(t.user_amount_with_charge) - amount)}</div>
              </div>

              <div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#111827" }}>{fmt(amount)}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>Total {fmt(debit)}</div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <StatusBadge status={t.transaction_status} />
                <button style={{ fontSize: 12, padding: "5px 10px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", color: "#374151", cursor: "pointer" }}>
                  Details
                </button>
                <button
                  onClick={() => setSelectedInvoice(t)}
                  style={{ fontSize: 12, padding: "5px 10px", border: "1px solid #bfdbfe", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer" }}
                >
                  Invoice
                </button>
              </div>
            </div>
          );
        })
      )}

      {selectedInvoice && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 80 }}>
          <div style={{ width: "100%", maxWidth: 760, height: "74vh", minHeight: 420, background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 60px rgba(15,23,42,0.18)", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #e5e7eb", background: "#fff", position: "relative", zIndex: 2, flexWrap: "wrap", gap: 8, minHeight: 72 }}>
              <div>
                <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 4 }}>Invoice report</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Transaction #{selectedInvoice.transaction_id}</div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={() => handleDownloadInvoicePdf(selectedInvoice)}
                  style={{ fontSize: 13, padding: "8px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}
                >
                  Download PDF
                </button>
                <button
                  onClick={() => handlePrintInvoice(selectedInvoice)}
                  style={{ fontSize: 13, padding: "8px 14px", background: "#fff", color: "#2563eb", border: "1px solid #2563eb", borderRadius: 8, cursor: "pointer" }}
                >
                  Print
                </button>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  style={{ fontSize: 13, padding: "8px 14px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 8, cursor: "pointer" }}
                >
                  Close
                </button>
              </div>
            </div>
            <div ref={invoiceRef} style={{ padding: 24, background: "#f9fafb", overflowY: "auto" }}>
              <div style={{ width: "100%", maxWidth: 860, margin: "0 auto", background: "#fff", borderRadius: 14, overflow: "hidden", border: "1px solid #d1d5db" }}>
                <div style={{ background: "#4f46e5", padding: "14px 20px", color: "#fff", fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Print Money Transfer Receipt
                </div>
                <div style={{ padding: "20px 24px", textAlign: "center", borderBottom: "1px solid #d1d5db", background: "#f8fafc" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.04em", color: "#111827" }}>DMT RECEIPT</div>
                  <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: "#111827" }}>#{selectedInvoice.transaction_id || "0000000000"}</div>
                </div>

                <div style={{ padding: "24px 24px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, rowGap: 10, borderBottom: "1px solid #d1d5db" }}>
                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151" }}>
                      <span>Date:</span>
                      <span>{new Date(selectedInvoice.transaction_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151" }}>
                      <span>Sender Number:</span>
                      <span>{selectedInvoice.beneficiary?.customer?.mobile_number || "—"}</span>
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151" }}>
                      <span>Account Number:</span>
                      <span>{selectedInvoice.beneficiary?.account_number || "—"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151" }}>
                      <span>Beneficiary Name:</span>
                      <span>{selectedInvoice.beneficiary?.account_holder_name || selectedInvoice.beneficiary?.customer?.customer_name || "—"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151" }}>
                      <span>IFSC Code:</span>
                      <span>{selectedInvoice.beneficiary?.ifsc_code || "—"}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151" }}>
                      <span>Transaction Type:</span>
                      <span>REMITTANCE IMPS</span>
                    </div>
                  </div>
                </div>

                <div style={{ padding: "20px 24px 18px" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.04em", color: "#111827", marginBottom: 12 }}>Transaction Details:</div>
                  <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #d1d5db" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1.5fr 1fr 1fr 1fr", gap: 0, background: "#eef2ff", padding: "14px 16px", fontSize: 11, fontWeight: 700, color: "#1f2937", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      <div>Sr. No.</div>
                      <div>TID</div>
                      <div>Amount</div>
                      <div>Status</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1.5fr 1fr 1fr 1fr", gap: 0, padding: "16px 16px", fontSize: 13, color: "#111827", background: "#fff" }}>
                      <div style={{ display: "grid", gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>1</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{new Date(selectedInvoice.transaction_date).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                      <div style={{ display: "grid", gap: 4 }}>
                        <div style={{ fontWeight: 700 }}>{selectedInvoice.transaction_id || "—"}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>IMPS/UTR No: {selectedInvoice.transaction_id || "—"}</div>
                      </div>
                      <div style={{ fontWeight: 700 }}>{fmt(Number(selectedInvoice.user_amount) || 0)}</div>
                      <div style={{ fontWeight: 700, color: selectedInvoice.transaction_status?.toLowerCase() === "success" ? "#15803d" : "#a32d2d" }}>{selectedInvoice.transaction_status?.toUpperCase() || "—"}</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 0, padding: "16px 16px", background: "#f8fafc", fontSize: 13, color: "#111827" }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Request Amount</div>
                        <div style={{ fontWeight: 700 }}>{fmt(Number(selectedInvoice.user_amount) || 0)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Success Amount</div>
                        <div style={{ fontWeight: 700 }}>{fmt(Number(selectedInvoice.user_amount) || 0)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Service Fee</div>
                        <div style={{ fontWeight: 700 }}>{fmt(serviceFeeAmount)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>Total Payable</div>
                        <div style={{ fontWeight: 700 }}>{fmt(totalPayableAmount)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: "0 10px 10px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, borderTop: "1px solid #d1d5db" }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Created By:</div>
                    <div style={{ fontSize: 13, color: "#111827", lineHeight: 1.6 }}>
                      S M TELECOM, Email: smcpro@gmail.com, Phone: 9619151713, Tel:, Address: vartak nagar, City: Thane, State: Maharashtra, Pincode: 400606, Country: India.
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 16, justifyContent: "end" }}>
                    <div style={{ display: "grid", gap: 4 }}>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Customer Sign:</div>
                      <div style={{ height: 30, borderBottom: "1px solid #d1d5db" }} />
                    </div>
                    <div style={{ display: "grid", gap: 4 }}>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Authorised By:</div>
                      <div style={{ height: 30, borderBottom: "1px solid #d1d5db" }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#9ca3af" }}>
            Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ width: 34, height: 34, border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: page === 1 ? "default" : "pointer", opacity: page === 1 ? 0.4 : 1, fontSize: 15 }}
            >‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                style={{ width: 34, height: 34, border: "1px solid", borderRadius: 6, fontSize: 13, cursor: "pointer", background: n === page ? "#2563eb" : "#fff", color: n === page ? "#fff" : "#374151", borderColor: n === page ? "#2563eb" : "#e5e7eb" }}
              >{n}</button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ width: 34, height: 34, border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff", cursor: page === totalPages ? "default" : "pointer", opacity: page === totalPages ? 0.4 : 1, fontSize: 15 }}
            >›</button>
          </div>
        </div>
      )}
    </div>
  );
}